import React, { useEffect, useState, ReactElement } from 'react';
import { HoverWrapper } from '@kingspan/design-system-components';
import AddIcon from '@mui/icons-material/Add';
import { Box, Grid, Stack, IconButton, Tooltip as MuiTooltip, TooltipProps, tooltipClasses, Typography } from '@mui/material';
import { PageSection } from 'components/PageSection/PageSection';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';

interface DetailsCardProps {
  cardTestId: string;
  buttonTestId: string;
  labelText: string;
  onClick: () => void;
  showTooltip?: boolean;
}

const Tooltip = styled(({ className, ...props }: TooltipProps) => <MuiTooltip {...props} classes={{ popper: className }} />)({
  [`& .${tooltipClasses.tooltip}`]: {
    padding: '10px',
    textAlign: 'center',
    borderRadius: '7px',
    backgroundColor: '#FFF4E5',
    color: '#653D00',
    fontFamily: 'KingspanBrownTT-Regular',
    fontSize: '16px',
    boxShadow: '0px 6px 12px -3px rgba(0,0,0,0.7)',
  },
});

const DetailsCardComponent = ({ cardTestId, buttonTestId, labelText, onClick, showTooltip }: DetailsCardProps): ReactElement => {
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(true);

  useEffect(() => {
    setTimeout(() => {
      setIsOpen(false);
    }, 2000);
  }, []);

  const onClickWrapper = (event: React.MouseEvent<HTMLDivElement, MouseEvent> | React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation();
    onClick();
  };

  const tooltipTitleMap: { [key: string]: string } = {
    'fastener-details-card': t('calculation-page.label-fasteners-tooltip'),
  };

  return (
    <Grid item xs={12} sm={6} xl={4} data-testid={cardTestId}>
      {showTooltip && (
        <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'flex-end' }}>
          <Box sx={{ position: 'absolute', mr: 1, mt: 1 }}>
            <Tooltip
              title={tooltipTitleMap[cardTestId]}
              placement="top"
              open={isOpen}
              onOpen={() => setIsOpen(true)}
              onClose={() => setIsOpen(false)}
            >
              <WarningAmberRoundedIcon style={{ color: '#FFB800' }} />
            </Tooltip>
          </Box>
        </Box>
      )}
      <HoverWrapper defaultBorder="lightgrey dotted 2px" defaultBorderWidth="2px">
        <PageSection
          onClick={(e) => onClickWrapper(e)}
          variant={'outlined'}
          sx={{ border: 'none', boxShadow: 'none', height: '150px', display: 'flex', justifyContent: 'center' }}
        >
          <Stack justifyContent="center" alignItems="center" spacing={2}>
            <IconButton className="primaryButton" data-testid={buttonTestId} onClick={(e) => onClickWrapper(e)}>
              <AddIcon style={{ color: 'white' }} />
            </IconButton>
            <Typography variant="h6" fontFamily="KingspanBrownTT-Bold">
              {labelText}
            </Typography>
          </Stack>
        </PageSection>
      </HoverWrapper>
    </Grid>
  );
};

export default DetailsCardComponent;
